import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Zap, CheckCircle2, XCircle, AlertTriangle, Loader2, Send, Trash2,
  RefreshCw, Link2, Clock, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api/integrations/highlevel`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });

const StatusBadge = ({ status }) => {
  const map = {
    connected: { cls: "bg-[#10B981]/15 text-[#10B981]", Icon: CheckCircle2, label: "Connected" },
    disconnected: { cls: "bg-white/10 text-[#94A3B8]", Icon: XCircle, label: "Disconnected" },
    expired: { cls: "bg-[#F59E0B]/15 text-[#F59E0B]", Icon: AlertTriangle, label: "Expired" },
    error: { cls: "bg-red-500/15 text-red-400", Icon: AlertTriangle, label: "Error" },
  };
  const { cls, Icon, label } = map[status] || map.error;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

export const GHLConnectPanel = ({ adminMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selConnection, setSelConnection] = useState("");
  const [selLocation, setSelLocation] = useState("");
  const [selListing, setSelListing] = useState("");
  const [busy, setBusy] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`, { headers: authHeader() });
      setData(res.data);
      const activeConn = (res.data.connections || []).find((c) => c.status === "connected");
      if (activeConn) setSelConnection((prev) => prev || activeConn.id);
    } catch (err) {
      console.error("GHL status fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Handle OAuth redirect result
  useEffect(() => {
    const ghl = searchParams.get("ghl");
    if (!ghl) return;
    if (ghl === "connected") {
      toast.success("GoHighLevel connected! Now map your listing to your CRM location below.");
    } else {
      const reason = searchParams.get("reason") || "unknown";
      toast.error(
        reason === "not_configured"
          ? "GoHighLevel app credentials are not configured yet. Contact your LocalBest admin."
          : `GoHighLevel connection failed (${reason}). Please try again.`
      );
    }
    searchParams.delete("ghl");
    searchParams.delete("reason");
    searchParams.delete("connection_id");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load locations when a connection is selected
  useEffect(() => {
    if (!selConnection) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/connections/${selConnection}/locations`, { headers: authHeader() });
        setLocations(res.data.locations || []);
        if ((res.data.locations || []).length === 1) setSelLocation(res.data.locations[0].id);
      } catch (err) {
        setLocations([]);
      }
    })();
  }, [selConnection]);

  const connect = () => {
    const token = localStorage.getItem("auth_token");
    window.location.href = `${API}/install?token=${encodeURIComponent(token)}&source=${adminMode ? "admin" : "client"}`;
  };

  const createMapping = async () => {
    if (!selListing || !selConnection || !selLocation) {
      toast.error("Pick a listing and a GHL location first");
      return;
    }
    setBusy("map");
    try {
      const res = await axios.post(
        `${API}/mappings`,
        { listing_id: selListing, connection_id: selConnection, ghl_location_id: selLocation },
        { headers: authHeader() }
      );
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to connect listing");
    } finally { setBusy(""); }
  };

  const toggleSync = async (mapping, enabled) => {
    try {
      await axios.patch(`${API}/mappings/${mapping.id}`, { sync_enabled: enabled }, { headers: authHeader() });
      toast.success(enabled ? "Lead sync enabled" : "Lead sync paused");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const sendTestLead = async (mapping) => {
    setBusy(`test-${mapping.id}`);
    try {
      const res = await axios.post(`${API}/mappings/${mapping.id}/test-lead`, {}, { headers: authHeader() });
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test lead failed");
    } finally { setBusy(""); }
  };

  const removeMapping = async (mapping) => {
    setBusy(`del-${mapping.id}`);
    try {
      const res = await axios.delete(`${API}/mappings/${mapping.id}`, { headers: authHeader() });
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove mapping");
    } finally { setBusy(""); }
  };

  const disconnect = async (conn) => {
    setBusy(`disc-${conn.id}`);
    try {
      const res = await axios.delete(`${API}/connections/${conn.id}`, { headers: authHeader() });
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(""); }
  };

  const retryLog = async (log) => {
    setBusy(`retry-${log.id}`);
    try {
      const res = await axios.post(`${API}/sync-logs/${log.id}/retry`, {}, { headers: authHeader() });
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally { setBusy(""); }
  };

  if (loading) {
    return (
      <div className="bg-[#0F172A] border border-white/10 rounded-xl p-6 flex items-center gap-3 text-[#94A3B8]">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading GoHighLevel status...
      </div>
    );
  }

  const connections = data?.connections || [];
  const mappings = data?.mappings || [];
  const listings = data?.listings || [];
  const recentLogs = data?.recent_logs || [];
  const activeConn = connections.find((c) => c.status === "connected");
  const unmappedListings = listings.filter(
    (l) => !mappings.some((m) => m.listing_id === l.id || m.listing_slug === l.slug)
  );

  return (
    <div className="bg-[#0F172A] border border-white/10 rounded-xl p-6 space-y-5" data-testid="ghl-connect-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F59E0B]" /> GoHighLevel CRM
          </h2>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Connect your GoHighLevel CRM so every lead from your LocalBest.ai listing, coupons,
            AI chat, call requests, and events automatically appears inside your CRM contacts.
          </p>
        </div>
        {!activeConn && (
          <Button onClick={connect} className="bg-[#F59E0B] hover:bg-[#FBBF24] text-black font-semibold" data-testid="ghl-connect-btn">
            <Link2 className="w-4 h-4 mr-2" /> Connect GoHighLevel
          </Button>
        )}
      </div>

      {data?.configured === false && (
        <div className="flex items-center gap-2 text-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-lg px-3 py-2" data-testid="ghl-not-configured">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          The GoHighLevel marketplace app credentials haven't been configured yet. The connect button will work once a LocalBest admin adds them.
        </div>
      )}

      {/* Connections */}
      {connections.length > 0 && (
        <div className="space-y-2">
          {connections.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-lg flex-wrap" data-testid={`ghl-connection-${c.id}`}>
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-4 h-4 text-[#F59E0B]" />
                <div>
                  <p className="text-white text-sm font-medium">
                    {c.ghl_location_name || c.ghl_company_name || (c.install_type === "agency" ? "GHL Agency" : "GHL Location")}
                    <span className="ml-2 text-xs text-[#64748B]">{c.install_type === "agency" ? "Agency install" : "Sub-account"}</span>
                  </p>
                  <p className="text-xs text-[#64748B] font-mono">{c.location_id || c.company_id}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
              <div className="flex items-center gap-2">
                {c.status !== "connected" && (
                  <Button size="sm" variant="outline" onClick={connect} className="border-white/10 text-white hover:bg-white/10" data-testid={`ghl-reconnect-${c.id}`}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconnect
                  </Button>
                )}
                {c.status === "connected" && (
                  <Button size="sm" variant="ghost" disabled={busy === `disc-${c.id}`} onClick={() => disconnect(c)}
                    className="text-[#64748B] hover:text-red-400 hover:bg-red-500/10" data-testid={`ghl-disconnect-${c.id}`}>
                    {busy === `disc-${c.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Disconnect"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create mapping */}
      {activeConn && unmappedListings.length > 0 && (
        <div className="border border-white/10 rounded-lg p-4 space-y-3" data-testid="ghl-mapping-form">
          <p className="text-sm font-medium text-white">Connect a listing to your CRM location</p>
          <div className="flex gap-3 flex-wrap">
            <select value={selListing} onChange={(e) => setSelListing(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[200px]"
              data-testid="ghl-listing-select">
              <option value="" className="bg-[#0F172A]">Choose your listing…</option>
              {unmappedListings.map((l) => (
                <option key={l.id || l.slug} value={l.id || l.slug} className="bg-[#0F172A]">{l.name}</option>
              ))}
            </select>
            <select value={selLocation} onChange={(e) => setSelLocation(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white min-w-[200px]"
              data-testid="ghl-location-select">
              <option value="" className="bg-[#0F172A]">Choose GHL location…</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id} className="bg-[#0F172A]">{loc.name || loc.id}</option>
              ))}
            </select>
            <Button onClick={createMapping} disabled={busy === "map"} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="ghl-create-mapping-btn">
              {busy === "map" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect Listing
            </Button>
          </div>
        </div>
      )}

      {/* Mappings */}
      {mappings.length > 0 && (
        <div className="space-y-2">
          {mappings.map((m) => (
            <div key={m.id} className="p-4 bg-white/5 rounded-lg space-y-3" data-testid={`ghl-mapping-${m.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-white text-sm font-medium">
                    {m.listing_name}
                    <span className="mx-2 text-[#64748B]">→</span>
                    <span className="text-[#F59E0B]">{m.ghl_location_name || m.ghl_location_id}</span>
                  </p>
                  <p className="text-xs text-[#64748B] flex items-center gap-2 mt-1">
                    {m.approval_status === "pending" && (
                      <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">Pending admin approval</span>
                    )}
                    {m.approval_status === "rejected" && (
                      <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">Rejected</span>
                    )}
                    {m.last_sync_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last sync: {m.last_sync_status === "success" ? "✓" : "✗"} {new Date(m.last_sync_at).toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs text-[#94A3B8]">
                    Sync
                    <Switch checked={!!m.sync_enabled} onCheckedChange={(v) => toggleSync(m, v)} data-testid={`ghl-sync-toggle-${m.id}`} />
                  </label>
                  <Button size="sm" variant="outline" disabled={busy === `test-${m.id}` || m.approval_status !== "approved"}
                    onClick={() => sendTestLead(m)}
                    className="border-white/10 text-white hover:bg-white/10" data-testid={`ghl-test-lead-${m.id}`}>
                    {busy === `test-${m.id}` ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                    Send Test Lead
                  </Button>
                  <button onClick={() => removeMapping(m)} disabled={busy === `del-${m.id}`}
                    className="p-1.5 rounded hover:bg-red-500/10 text-[#64748B] hover:text-red-400" data-testid={`ghl-remove-mapping-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent sync activity */}
      {recentLogs.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2">Recent Lead Sync Activity</p>
          <div className="space-y-1">
            {recentLogs.slice(0, 5).map((log) => (
              <div key={log.id} className="flex items-center justify-between gap-2 text-xs px-3 py-2 bg-white/5 rounded" data-testid={`ghl-log-${log.id}`}>
                <span className="text-[#94A3B8] truncate">
                  {log.sync_status === "success" ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] inline mr-1.5" />
                  ) : (
                    <XCircle className="w-3.5 h-3.5 text-red-400 inline mr-1.5" />
                  )}
                  {log.action === "contact_upsert" ? (log.is_test ? "Test lead" : "Lead") : log.action} → {log.listing_name || log.listing_slug || log.ghl_location_id}
                  <span className="text-[#64748B] ml-2">{new Date(log.created_at).toLocaleString()}</span>
                </span>
                {(log.sync_status === "failed" || log.sync_status === "retrying") && (
                  <Button size="sm" variant="ghost" disabled={busy === `retry-${log.id}`} onClick={() => retryLog(log)}
                    className="h-6 px-2 text-[#F59E0B] hover:bg-[#F59E0B]/10" data-testid={`ghl-retry-${log.id}`}>
                    {busy === `retry-${log.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry Now"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!activeConn && connections.length === 0 && (
        <p className="text-sm text-[#64748B]">
          No CRM connected yet. Click <span className="text-white">Connect GoHighLevel</span>, approve access for your
          sub-account, then pick which listing sends leads to it.
        </p>
      )}
    </div>
  );
};

export default GHLConnectPanel;
